import { useState } from 'react';
import { api } from '../../api/client.js';
import { registerPasskey } from '../../utils/webauthn.js';
import { Modal } from '../../components/Modal.jsx';
import { PasswordField } from '../../components/forms/PasswordField.jsx';

export function SecuritySettingsPage() {
  return (
    <div className="page security-page">
      <div className="page-header">
        <div>
          <h1>Security</h1>
          <p>Protect access to your restaurant account and menu content.</p>
        </div>
      </div>
      <div className="security-notice">
        <strong>Keep your account protected</strong>
        <span>Use a unique password, and add a passkey if your device supports it for faster, safer sign-in.</span>
      </div>
      <div className="security-panels">
        <PasswordSection />
        <PasskeySection />
      </div>
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setMessage(null);
    setSubmitting(true);
    try {
      await api.patch('/api/tenant-admin/auth/password', { currentPassword, newPassword });
      setMessage({ type: 'success', text: 'Password changed. You have been signed out of your other devices.' });
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="panel security-panel">
      <h2>Password</h2>
      <p>Changing your password signs you out of other devices.</p>
      {message && <p className={`alert alert-${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</p>}
      <form onSubmit={handleSubmit}>
        <PasswordField id="current-password" label="Current password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
        <PasswordField id="new-password" label="New password" autoComplete="new-password" minLength={8} placeholder="At least 8 characters" hint="At least 8 characters." value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={submitting} aria-busy={submitting || undefined}>
          {submitting ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </div>
  );
}

function PasskeySection() {
  const [message, setMessage] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [deviceName, setDeviceName] = useState('My device');

  async function handleRegister(event) {
    event.preventDefault();
    setMessage(null);
    setRegistering(true);
    try {
      await registerPasskey('tenant-admin', deviceName.trim() || undefined);
      setMessage({ type: 'success', text: 'Passkey added. You can now sign in with it from this device.' });
      setShowRegister(false);
    } catch {
      setMessage({ type: 'error', text: 'Could not add a passkey. Your device may not support them, or the request was cancelled.' });
    } finally {
      setRegistering(false);
    }
  }

  return (
    <div className="panel security-panel">
      <h2>Passkeys</h2>
      <p>Optionally sign in without a password using your device's built-in passkey support (Face ID, Touch ID, Windows Hello, etc).</p>
      {message && <p className={`alert alert-${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>{message.text}</p>}
      <button type="button" className="btn btn-primary" onClick={() => setShowRegister(true)} disabled={registering} aria-busy={registering || undefined}>
        {registering ? 'Waiting for device…' : '+ Add a passkey'}
      </button>
      {showRegister && (
        <Modal onClose={() => setShowRegister(false)} labelledBy="passkey-dialog-title" busy={registering}>
          <h2 id="passkey-dialog-title">Add a passkey</h2>
          <p className="modal-description">Give this device a name so you can recognize it later.</p>
          <form onSubmit={handleRegister}>
            <div className="field">
              <label htmlFor="passkey-device-name">Device name</label>
              <input id="passkey-device-name" type="text" placeholder="e.g. My laptop" value={deviceName} onChange={(event) => setDeviceName(event.target.value)} />
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn" disabled={registering} onClick={() => setShowRegister(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={registering}>{registering ? 'Waiting for device…' : 'Continue'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
